import { useState } from "react";
import { Popover, MenuItem, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { SettlementSheet } from "./settlement-sheet";
import ManualSettlementDialog from "@/components/settlement/manual-settlement-dialog";
import { usePermission } from "@/hooks/use-permissions";
import { CAPABILITIES } from "@/config/permissions";
import { useManualSettlement } from "@/hooks/use-manual-settlement";
import { getSettlementManualEligibility } from "@/utils/manual-settlement";
import { formatCurrency } from "@/utils/format-currency";
import { showToast } from "@/redux/actions/toast-actions";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  "&:hover": { backgroundColor: theme.palette.action.hover },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 200,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export function SettlementActionsPopover({ settlement }) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const isOpen = Boolean(anchorEl);

  const canSettleManually = usePermission(CAPABILITIES.SETTLEMENT_MANUAL);
  const { eligible, reason, notice } = getSettlementManualEligibility(settlement);
  const settlementMutation = useManualSettlement();

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleView = () => {
    handleClose();
    setSheetOpen(true);
  };

  const handleSettleClick = () => {
    handleClose();
    setSettleOpen(true);
  };

  const handleSettle = (reference) => {
    settlementMutation.mutate(
      { paymentRefNo: reference },
      {
        onSuccess: (res) => {
          dispatch(showToast(res?.message ?? "Settlement processed", "success"));
          setSettleOpen(false);
        },
        onError: (err) =>
          dispatch(showToast(err?.message ?? "Settlement could not be processed", "error")),
      },
    );
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { elevation: 2, sx: { borderRadius: "8px", mt: 0.5 } } }}
      >
        <PopoverContent>
          <StyledMenuItem onClick={handleView}>
            <VisibilityIcon fontSize="small" />
            View
          </StyledMenuItem>

          {/* Absent entirely without the capability; disabled with the reason
              when this particular settlement cannot be settled by hand. */}
          {canSettleManually && (
            <Tooltip title={eligible ? "" : (reason ?? "")} arrow placement="left">
              <span>
                <StyledMenuItem
                  disabled={!eligible}
                  onClick={handleSettleClick}
                  sx={{ width: "100%" }}
                >
                  <PaidOutlinedIcon fontSize="small" />
                  Settle Manually
                </StyledMenuItem>
              </span>
            </Tooltip>
          )}
        </PopoverContent>
      </Popover>

      <SettlementSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        settlement={settlement}
      />

      <ManualSettlementDialog
        open={settleOpen}
        onClose={() => setSettleOpen(false)}
        reference={settlement?.paymentRefNo}
        rows={[
          { label: "Settlement Ref", value: settlement?.settlementReferenceId },
          { label: "Payment Ref", value: settlement?.paymentRefNo },
          { label: "Merchant", value: settlement?.merchantName ?? settlement?.merchantId },
          { label: "Gross", value: formatCurrency(settlement?.settlementGrossAmount) },
          { label: "Fee", value: formatCurrency(settlement?.totalFee) },
          { label: "Net to settle", value: formatCurrency(settlement?.settlementNetAmount) },
        ]}
        notice={notice}
        mutation={settlementMutation}
        onConfirm={handleSettle}
      />
    </>
  );
}
